/**
 * JS handles for the CSS variables in src/styles/tokens.css, for MUI `sx`.
 * Each value carries its fallback so components still render in tests
 * (jsdom does not load the stylesheet).
 */
const v = (name, fallback) => `var(--evx-${name}, ${fallback})`;

export const COLOR = {
    bg: v('bg', '#0a0a0a'),
    surface: v('surface', '#18181b'),
    surface2: v('surface-2', '#202024'),
    overlay: v('overlay', 'rgba(0, 0, 0, 0.78)'),
    border: v('border', '#2e2e33'),
    borderStrong: v('border-strong', '#3f3f46'),
    text: v('text', '#fafafa'),
    textMuted: v('text-muted', '#a1a1aa'),
    textSubtle: v('text-subtle', '#8b8b94'),
    primary: v('primary', '#2cbb5d'),
    primaryHover: v('primary-hover', '#25a352'),
    primaryContrast: v('primary-contrast', '#04120a'),
    danger: v('danger', '#ef4743'),
    warning: v('warning', '#fbbf24'),
    info: v('info', '#38bdf8'),
    gold: v('gold', '#fbbf24'),
    focus: v('focus', '#7dd3fc'),
};

export const FONT = {
    sans: v('font-sans', 'sans-serif'),
    mono: v('font-mono', 'monospace'),
};

export const FS = {
    xs: v('fs-xs', '0.75rem'),
    sm: v('fs-sm', '0.875rem'),
    md: v('fs-md', '1rem'),
    lg: v('fs-lg', '1.25rem'),
    xl: v('fs-xl', '1.5rem'),
    xxl: v('fs-2xl', '2rem'),
    xxxl: v('fs-3xl', '2.5rem'),
};

export const SPACE = {
    1: v('space-1', '4px'),
    2: v('space-2', '8px'),
    3: v('space-3', '12px'),
    4: v('space-4', '16px'),
    5: v('space-5', '24px'),
    6: v('space-6', '32px'),
    7: v('space-7', '48px'),
    8: v('space-8', '64px'),
};

export const RADIUS = {
    sm: v('radius-sm', '6px'),
    md: v('radius-md', '10px'),
    lg: v('radius-lg', '16px'),
    pill: v('radius-pill', '999px'),
};

export const SHADOW = {
    1: v('shadow-1', '0 4px 12px rgba(0,0,0,0.25)'),
    2: v('shadow-2', '0 12px 40px rgba(0,0,0,0.55)'),
    focus: v('focus-ring', '0 0 0 3px #7dd3fc'),
};

export const DURATION = v('dur', '150ms');

/** Visible keyboard focus, shared by every interactive Arcade control. */
export const FOCUS_VISIBLE_SX = {
    '&:focus-visible': { outline: 'none', boxShadow: SHADOW.focus },
};

/**
 * Curated game accents: 8 hues that keep at least 4.5:1 contrast on the
 * dark surfaces. Each game takes one; accents mark the title word, the board
 * frame and score chips, never a button fill.
 */
export const ACCENT_PALETTE = {
    violet: '#a5a3f0',
    teal: '#2dd4bf',
    sky: '#38bdf8',
    amber: '#fbbf24',
    orange: '#fb923c',
    rose: '#fb7185',
    lime: '#a3e635',
    pink: '#f472b6',
};

export const GAME_ACCENT = {
    adventure_quest: ACCENT_PALETTE.orange,
    space_invader: ACCENT_PALETTE.teal,
    sliding_tiles_puzzle: ACCENT_PALETTE.sky,
    santa_drop: ACCENT_PALETTE.rose,
    tetris: ACCENT_PALETTE.teal,
    snake2d: ACCENT_PALETTE.lime,
    tic_tac_toe: ACCENT_PALETTE.teal,
    pong: ACCENT_PALETTE.orange,
    connect_four: ACCENT_PALETTE.amber,
    two_joint_pole: ACCENT_PALETTE.violet,
    one_armed_bandit: ACCENT_PALETTE.amber,
    blackjack: ACCENT_PALETTE.teal,
    lights_out: ACCENT_PALETTE.amber,
    battleship: ACCENT_PALETTE.sky,
    numberlink: ACCENT_PALETTE.orange,
    minesweeper: ACCENT_PALETTE.sky,
    road_fighter: ACCENT_PALETTE.rose,
    pac_man: ACCENT_PALETTE.amber,
};
